// src/components/reports/ProjectOverviewCard.js
import React from "react";
import { Calendar, AlertTriangle, CheckCircle, Clock } from "lucide-react";

/**
 * ProjectOverviewCard
 *
 * Props:
 * - project: {
 *     name,
 *     description,
 *     startDate,
 *     endDate,
 *     deadlineStatus: "active" | "overdue" | "not_set"
 *   }
 */
export default function ProjectOverviewCard({ project }) {
  if (!project) return null;

  const {
    name,
    description,
    startDate,
    endDate,
    deadlineStatus,
  } = project;

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString() : "—";

  const statusMeta = {
    active: {
      label: "On track",
      icon: CheckCircle,
      color: "text-emerald-600 bg-emerald-50 border-emerald-200",
    },
    overdue: {
      label: "Overdue",
      icon: AlertTriangle,
      color: "text-rose-600 bg-rose-50 border-rose-200",
    },
    not_set: {
      label: "No deadline",
      icon: Clock,
      color: "text-gray-600 bg-gray-50 border-gray-200",
    },
  };

  const status = statusMeta[deadlineStatus] || statusMeta.not_set;
  const StatusIcon = status.icon;

  return (
    <div className="rounded-xl border bg-white p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {name}
          </h3>
          {description && (
            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
              {description}
            </p>
          )}
        </div>

        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${status.color}`}
        >
          <StatusIcon className="w-4 h-4" />
          {status.label}
        </span>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span>
            <span className="font-medium">Start:</span>{" "}
            {formatDate(startDate)}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span>
            <span className="font-medium">End:</span>{" "}
            {formatDate(endDate)}
          </span>
        </div>
      </div>
    </div>
  );
}